import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import TokenCostModal from '../../components/TokenCostModal';
import { apiClient } from '../../lib/api';
import { useAuthStore } from '../../lib/auth';

// Type definitions matching backend schema
interface ContentSource {
  id: string;
  workspaceId: string;
  websiteId?: string | null;
  type: 'blog' | 'product' | 'service' | 'other';
  url: string;
  title?: string | null;
  discoverySource: 'llm.txt' | 'sitemap.xml' | 'homepage' | 'manual';
  discoveryConfidence?: string | null;
  lastScrapedAt?: string | null;
  isActive: boolean;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

// Validation schema for creating/editing sources
const contentSourceSchema = z.object({
  type: z.enum(['blog', 'product', 'service', 'other']),
  url: z.string().url('Please enter a valid URL'),
  title: z.string().optional(),
});

type ContentSourceForm = z.infer<typeof contentSourceSchema>;

// Estimated token cost for content discovery (AI categorization with GPT-4o-mini)
const CONTENT_DISCOVERY_TOKEN_COST = 1500;

export default function ContentSettingsPage() {
  const { t } = useTranslation();
  const { workspace } = useAuthStore();
  const [sources, setSources] = useState<ContentSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('');
  const [filterWebsite, setFilterWebsite] = useState<string>('');
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContentSourceForm>({
    resolver: zodResolver(contentSourceSchema),
  });

  // Fetch content sources
  const fetchSources = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (filterType) params.type = filterType;
      if (filterActive) params.isActive = filterActive;
      if (filterWebsite) params.websiteId = filterWebsite;

      const response = await apiClient.get('/content-sources', { params });
      setSources(response.data.data);
      setError('');
    } catch (err: any) {
      console.error('Failed to fetch content sources:', err);
      setError('Failed to load content sources');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, [filterType, filterActive, filterWebsite]);

  const onSubmit = async (data: ContentSourceForm) => {
    setError('');
    setSuccessMessage('');

    try {
      if (editingId) {
        // Update existing source
        await apiClient.patch(`/content-sources/${editingId}`, data);
        setSuccessMessage('Content source updated successfully');
        setEditingId(null);
      } else {
        // Create new source
        await apiClient.post('/content-sources', {
          ...data,
          discoverySource: 'manual',
        });
        setSuccessMessage('Content source added successfully');
        setIsAdding(false);
      }

      reset();
      fetchSources();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to save content source:', err);
      setError(err.response?.data?.message || 'Failed to save content source');
    }
  };

  const handleEdit = (source: ContentSource) => {
    setEditingId(source.id);
    reset({
      type: source.type,
      url: source.url,
      title: source.title || undefined,
    });
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/content-sources/${id}`);
      setSuccessMessage('Content source deleted successfully');
      fetchSources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to delete content source:', err);
      setError(err.response?.data?.message || 'Failed to delete content source');
    }
  };

  const handleToggleActive = async (source: ContentSource) => {
    try {
      await apiClient.patch(`/content-sources/${source.id}`, {
        isActive: !source.isActive,
      });
      setSuccessMessage(
        `Content source ${source.isActive ? 'deactivated' : 'activated'} successfully`
      );
      fetchSources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to toggle content source:', err);
      setError(err.response?.data?.message || 'Failed to update content source');
    }
  };

  // Execute content discovery
  const handleConfirmDiscovery = async () => {
    const websiteUrl = workspace?.settings?.onboarding?.website;

    if (!websiteUrl) {
      setError('No website URL configured. Please add a website URL in Company Settings first.');
      throw new Error('No workspace website URL found');
    }

    setError('');
    setSuccessMessage('');
    setIsDiscovering(true);

    // Call content discovery endpoint
    const response = await apiClient.post('/utils/discover-content', {
      url: websiteUrl,
    });

    const { blogUrls = [], productUrls = [] } = response.data;

    // Create content sources from discovered URLs
    let createdCount = 0;
    for (const discovered of [...blogUrls, ...productUrls]) {
      try {
        await apiClient.post('/content-sources', {
          type: discovered.type,
          url: discovered.url,
          title: discovered.title,
          discoverySource: response.data.source || 'homepage',
        });
        createdCount++;
      } catch (err: any) {
        console.error('Failed to create content source:', discovered.url, err);
      }
    }

    // Refresh sources list
    await fetchSources();

    setSuccessMessage(
      `Auto-discovery complete! ${createdCount} new content sources added.`
    );
    setIsDiscovering(false);

    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'blog':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'product':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'service':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  const getDiscoveryBadge = (source: string): string => {
    switch (source) {
      case 'llm.txt':
        return 'LLM.txt';
      case 'sitemap.xml':
        return 'Sitemap';
      case 'homepage':
        return 'Homepage';
      default:
        return 'Manual';
    }
  };

  // Group sources by type
  const groupedSources = sources.reduce((acc, source) => {
    if (!acc[source.type]) {
      acc[source.type] = [];
    }
    acc[source.type].push(source);
    return acc;
  }, {} as Record<string, ContentSource[]>);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2 dark:text-white">Content Sources</h1>
          <p className="text-gray-600 dark:text-gray-400">Loading content sources...</p>
        </div>
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2 dark:text-white">Content Sources</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your blog posts, products, and other content URLs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowDiscoveryModal(true)}
            variant="secondary"
            disabled={isDiscovering || isAdding || !workspace?.settings?.onboarding?.website}
            title={!workspace?.settings?.onboarding?.website ? 'Please configure a website URL in Company Settings first' : 'Automatically discover content from your website'}
          >
            {isDiscovering ? (
              'Discovering...'
            ) : (
              `Auto-Discover (${CONTENT_DISCOVERY_TOKEN_COST.toLocaleString()} tokens)`
            )}
          </Button>
          {!isAdding && (
            <Button onClick={() => setIsAdding(true)}>Add Source</Button>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-3 border-2 border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200 text-sm">
          {successMessage}
        </div>
      )}

      {/* Add/Edit Form */}
      {isAdding && (
        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <h2 className="text-xl font-bold dark:text-white">
              {editingId ? 'Edit Content Source' : 'Add Content Source'}
            </h2>

            <Select
              {...register('type')}
              label="Type"
              error={errors.type?.message}
            >
              <option value="">Select type</option>
              <option value="blog">Blog Post</option>
              <option value="product">Product</option>
              <option value="service">Service</option>
              <option value="other">Other</option>
            </Select>

            <Input
              {...register('url')}
              label="URL"
              type="url"
              placeholder="https://example.com/blog/post"
              error={errors.url?.message}
            />

            <Input
              {...register('title')}
              label="Title (Optional)"
              placeholder="Blog post title"
              error={errors.title?.message}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsAdding(false);
                  setEditingId(null);
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <Select
            label="Filter by Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="blog">Blog</option>
            <option value="product">Product</option>
            <option value="service">Service</option>
            <option value="other">Other</option>
          </Select>

          <Select
            label="Filter by Status"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>

          <Select
            label="Filter by Website"
            value={filterWebsite}
            onChange={(e) => setFilterWebsite(e.target.value)}
          >
            <option value="">All Websites</option>
            {workspace?.websites && workspace.websites.map((website) => (
              <option key={website.id} value={website.id}>
                {website.name}
              </option>
            ))}
          </Select>

          {(filterType || filterActive || filterWebsite) && (
            <Button
              variant="secondary"
              onClick={() => {
                setFilterType('');
                setFilterActive('');
                setFilterWebsite('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Content Sources List */}
      {Object.keys(groupedSources).length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No content sources found. Add your first source to get started.
            </p>
            <Button onClick={() => setIsAdding(true)}>Add Source</Button>
          </div>
        </Card>
      ) : (
        Object.entries(groupedSources).map(([type, typeSources]) => (
          <Card key={type}>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-bold capitalize dark:text-white">{type}</h2>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                ({typeSources.length})
              </span>
            </div>

            <div className="space-y-3">
              {typeSources.map((source) => (
                <div
                  key={source.id}
                  className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(source.type)}`}>
                        {source.type}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getDiscoveryBadge(source.discoverySource)}
                      </span>
                      {!source.isActive && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                          Inactive
                        </span>
                      )}
                    </div>

                    {source.title && (
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                        {source.title}
                      </h3>
                    )}

                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                    >
                      {source.url}
                    </a>

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>Added: {formatDate(source.createdAt)}</span>
                      {source.discoveryConfidence && (
                        <span>Confidence: {source.discoveryConfidence}%</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="secondary"
                      onClick={() => handleToggleActive(source)}
                    >
                      {source.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleEdit(source)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleDelete(source.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))
      )}

      {/* Summary */}
      <Card>
        <div className="flex justify-between items-center text-sm">
          <p className="text-gray-600 dark:text-gray-400">
            Total: {sources.length} sources •{' '}
            {sources.filter((s) => s.isActive).length} active •{' '}
            {sources.filter((s) => !s.isActive).length} inactive
          </p>
        </div>
      </Card>

      {/* Token Cost Modal */}
      <TokenCostModal
        open={showDiscoveryModal}
        onClose={() => setShowDiscoveryModal(false)}
        onConfirm={handleConfirmDiscovery}
        title={t('tokenCostModal.contentDiscovery.title')}
        description={t('tokenCostModal.contentDiscovery.description')}
        tokenCost={CONTENT_DISCOVERY_TOKEN_COST}
        confirmButtonText={t('tokenCostModal.contentDiscovery.confirm')}
        estimatedDuration={t('tokenCostModal.contentDiscovery.duration')}
      />
    </div>
  );
}
